import type {
    BaseMessage,
    FileMessage,
    UserMessage,
} from '@sendbird/chat/message';
import { MessageType } from '@sendbird/chat/message';
import { Image, Platform } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteEmphasized,
    isWeb,
    Text,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { Guide } from '../../guidance/types/Guide';
import { useShowGuideMessaging } from '../hooks/useShowGuideMessaging';

interface LatestGuideMessageCtaProps {
    guide?: Guide | null;
    message: UserMessage | FileMessage;
}

const LatestGuideMessageCta = ({
    guide,
    message,
}: LatestGuideMessageCtaProps) => {
    const showGuideMessagingModal = useShowGuideMessaging();

    const guideSenderId = guide?.sendbirdUserId ?? null;

    const { sender } = message;
    const { userId: latestMessageUserId } = sender;
    const [senderFirstName = '', senderLastName = ''] =
        sender?.nickname.split(' ') ?? '';
    const senderInitials =
        senderFirstName?.slice(0, 1) + senderLastName?.slice(0, 1);

    const guideSentLastMessage = guideSenderId === latestMessageUserId;
    const messageContent = getMessageContent(message);

    const CTAMessage = guideSentLastMessage
        ? `Reply to ${guide?.firstName}`
        : `Message ${guide?.firstName}`;

    const shadowStyle = Platform.select({
        ios: {
            shadowColor: 'rgba(0, 0, 0, 0.05)',
            shadowOffset: {
                width: 0,
                height: 5,
            },
            shadowOpacity: 0.05,
            shadowRadius: 10,
        },
        android: {
            elevation: 5,
        },
    });

    const navigateToGuideMessaging = () => {
        showGuideMessagingModal();
    };

    return (
        <YStack
            borderRadius={20}
            borderWidth={1}
            borderColor="$gray200"
            backgroundColor="$white"
            paddingHorizontal={20}
            paddingTop={16}
            paddingBottom={30}
            style={{ ...shadowStyle }}
            onPress={() => {
                !isWeb && navigateToGuideMessaging();
            }}
            $sm={{ flex: 1, height: 400 }}
        >
            <View
                borderRadius={16}
                backgroundColor="$gray100"
                paddingHorizontal={12}
                paddingVertical={6}
                marginVertical={8}
                $sm={{ maxHeight: 225, overflowY: 'auto' }} // max height is necessary for web given the fixed height of parent
            >
                <BodyMediumRegular whiteSpace="pre-wrap">
                    {messageContent}
                </BodyMediumRegular>
            </View>

            <XStack
                width="100%"
                marginTop={14}
                marginBottom={32}
                alignItems="center"
            >
                {guideSentLastMessage ? (
                    <Image
                        source={{ uri: guide?.profileImageUrl ?? '' }}
                        style={{ width: 28, height: 28, borderRadius: 20 }}
                    />
                ) : (
                    <UserInitials senderInitials={senderInitials} />
                )}
                <FootnoteEmphasized marginLeft={12} color="$gray1000">
                    {senderFirstName}
                </FootnoteEmphasized>
            </XStack>

            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                text={CTAMessage}
                onPress={navigateToGuideMessaging}
            />
        </YStack>
    );
};

const getMessageContent = (message: BaseMessage): string => {
    switch (message.messageType) {
        case MessageType.FILE: {
            const fileMessage = message as FileMessage;
            const senderFirstName =
                fileMessage.sender?.nickname.split(' ')[0] ?? 'your Guide';
            const fileName = fileMessage.messageParams?.fileName ?? '';
            return `New file ${
                fileName ? `${fileName} ` : ''
            }uploaded by ${senderFirstName}`;
        }
        default:
            return getPlainTextMessageContent((message as UserMessage).message);
    }
};

/**
 * Messages support bold and embedded links in the message content for Member and Guide chats. Some places where
 * these messages are rendered, such as email and push notifications, do not support markdown-like formatting.
 * This method strips the formatting characters from the message content.
 * @param messageContent the raw message from Sendbird that may contain markdown-like formatting
 * @returns a plain text version of the message content, without the formatting characters
 */
const getPlainTextMessageContent = (messageContent: string): string => {
    // Remove bold formatting
    messageContent = messageContent.replaceAll('**', '');

    // Remove link formatting
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g; // Matches [text](url)
    messageContent = messageContent.replace(linkRegex, '$1 ($2)');

    return messageContent;
};

const UserInitials = ({ senderInitials }: { senderInitials: string }) => {
    return (
        <View
            width={28}
            height={28}
            borderRadius={20}
            backgroundColor="$gray100"
            justifyContent="center"
        >
            <Text
                color="$gray1000"
                textAlign="center"
                fontSize={12}
                lineHeight={16}
            >
                {senderInitials}
            </Text>
        </View>
    );
};

export default LatestGuideMessageCta;
